import { useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { Link } from "react-router-dom";
import api from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications?limit=15");
      setItems(data.items);
      setUnread(data.unread);
    } catch (_e) {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => { clearInterval(t); document.removeEventListener("mousedown", onClick); };
  }, []);

  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };
  const markAll = async () => {
    await api.post(`/notifications/read-all`);
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-full pf-btn-glass flex items-center justify-center"
        data-testid="notif-bell"
        aria-label="Notifications"
      >
        <Bell className="w-4 h-4 text-slate-700" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 text-white text-[10px] font-bold flex items-center justify-center shadow" data-testid="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-w-[92vw] glass-strong rounded-2xl py-2 shadow-xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="font-display text-base font-medium text-slate-900">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs font-semibold text-cyan-700 hover:underline flex items-center gap-1" data-testid="notif-mark-all">
                <CheckCheck className="w-3.5 h-3.5" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">You're all caught up ✨</div>
            ) : (
              items.map((n) => (
                <Link
                  to={n.link || "#"}
                  key={n.id}
                  onClick={() => { setOpen(false); markOne(n.id); }}
                  className={`block px-4 py-3 mx-1 rounded-xl hover:bg-white/60 border-l-2 transition-colors ${n.read ? "border-transparent" : "border-cyan-500 bg-cyan-50/40"}`}
                  data-testid={`notif-item-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.read ? "bg-slate-300" : "bg-cyan-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm font-medium truncate ${n.read ? "text-slate-700" : "text-slate-900"}`}>{n.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.message}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold whitespace-nowrap">{timeAgo(n.created_at)}</div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
